// PlayerMerge.js
import React, { useState } from 'react'
import ErrorModal from '../../shared/components/UIElements/ErrorModal'
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner'
import AdminNavigation from './AdminNavigation'
import SearchForPlayerPicker from '../../shared/components/UIElements/SearchForPlayerPicker'

const PlayerMerge = () => {
	const [error, setError] = useState(null)

	// selected Player docs
	// NOTE: per your request:
	// LEFT = OLD (source / merge-from)
	// RIGHT = NEW (target / keep)
	const [mergePlayer, setMergePlayer] = useState(null) // older / source
	const [keepPlayer, setKeepPlayer] = useState(null) // newest / target

	const [confirmChecked, setConfirmChecked] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const errorHandler = () => setError(null)

	const reset = () => {
		setKeepPlayer(null)
		setMergePlayer(null)
		setConfirmChecked(false)
	}

	const canMerge =
		keepPlayer &&
		mergePlayer &&
		String(keepPlayer._id) !== String(mergePlayer._id) &&
		confirmChecked &&
		!isSubmitting

	const submit = async () => {
		if (!canMerge) return

		setIsSubmitting(true)
		try {
			// TODO backend route you’ll create:
			const resp = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/admin/mergePlayers`,
				{
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						keepPlayerId: keepPlayer._id,
						mergePlayerId: mergePlayer._id,
					}),
				},
			)

			const data = await resp.json()
			if (!resp.ok) throw new Error(data.message || 'Merge failed')

			if (data?.committed?.mergedPlayer_deleted !== 1) {
				throw new Error('Merge did not delete the old player.  Maybe try again')
			}

			alert('Merge complete.')
			reset()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsSubmitting(false)
		}
	}

	const InfoRow = ({ label, value }) => (
		<div style={{ marginBottom: '.25rem' }}>
			<b>{label}:</b> {value || '—'}
		</div>
	)

	const PlayerCard = ({ title, p }) => (
		<div
			style={{
				background: 'white',
				borderRadius: '12px',
				boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
				padding: '1rem',
				marginBottom: '1rem',
			}}
		>
			<h3 style={{ marginTop: 0 }}>{title}</h3>
			{!p && (
				<p style={{ opacity: 0.75, margin: 0 }}>No player selected yet.</p>
			)}
			{p && (
				<div style={{ fontSize: '.95rem' }}>
					<InfoRow
						label='Name'
						value={`${p.firstName} ${p.middleInitial || ''} ${p.lastName}`}
					/>
					<InfoRow label='USABH' value={p.usabh} />
					<InfoRow label='PlayerId' value={p._id} />
				</div>
			)}
		</div>
	)

	return (
		<div style={{ overflowX: 'hidden' }}>
			<AdminNavigation />
			<ErrorModal error={error} onClear={errorHandler} />

			<div style={{ maxWidth: '1100px', margin: '0 auto', padding: '1rem' }}>
				<h1 style={{ marginTop: '4rem' }}>Player Merge</h1>
				<p style={{ marginTop: '.25rem', opacity: 0.8, textAlign: 'center' }}>
					Select two <b>Players</b> (same person). You’ll merge the older record
					into the newer one.
				</p>

				<div className='row' style={{ marginTop: '1rem' }}>
					{/* LEFT = OLD / MERGE-FROM */}
					<div className='col-lg-6 col-sm-12'>
						<SearchForPlayerPicker
							label='OLD (merge from / source)'
							onSelectPlayer={(p) => {
								setMergePlayer(p)
								setConfirmChecked(false)
							}}
							disableSelfId={keepPlayer?._id}
						/>
						<PlayerCard title='Old Player (Source)' p={mergePlayer} />
					</div>

					{/* RIGHT = NEW / KEEP */}
					<div className='col-lg-6 col-sm-12'>
						<SearchForPlayerPicker
							label='NEW (keep / target)'
							onSelectPlayer={(p) => {
								setKeepPlayer(p)
								setConfirmChecked(false)
							}}
							disableSelfId={mergePlayer?._id}
						/>
						<PlayerCard title='New Player (Target)' p={keepPlayer} />
					</div>
				</div>

				<div
					style={{
						background: '#fafafa',
						border: '1px solid #eee',
						borderRadius: '12px',
						padding: '1rem',
						marginTop: '1rem',
					}}
				>
					<h3 style={{ marginTop: 0 }}>Confirm</h3>

					{keepPlayer && mergePlayer && (
						<p style={{ marginTop: 0 }}>
							<b>NEW (KEEP):</b> {keepPlayer.firstName} {keepPlayer.lastName} (
							{keepPlayer.usabh})
							<br />
							<b>OLD (MERGE):</b> {mergePlayer.firstName} {mergePlayer.lastName}{' '}
							({mergePlayer.usabh})
						</p>
					)}

					<div style={{ display: 'flex', gap: '.75rem', alignItems: 'center' }}>
						<input
							type='checkbox'
							checked={confirmChecked}
							onChange={(e) => setConfirmChecked(e.target.checked)}
						/>
						<div style={{ fontSize: '.95rem' }}>
							I understand this will move all references from the older player
							into the newer one.
						</div>
					</div>

					<div style={{ display: 'flex', gap: '.75rem', marginTop: '1rem' }}>
						<button
							onClick={submit}
							disabled={!canMerge}
							style={{
								padding: '.75rem 1rem',
								borderRadius: '10px',
								border: 'none',
								fontWeight: 800,
								cursor: canMerge ? 'pointer' : 'not-allowed',
							}}
						>
							{isSubmitting ? 'Merging...' : 'MERGE'}
						</button>

						<button
							onClick={reset}
							style={{
								padding: '.75rem 1rem',
								borderRadius: '10px',
								border: '1px solid #ddd',
								background: 'white',
								fontWeight: 700,
								cursor: 'pointer',
							}}
						>
							Reset
						</button>
					</div>

					{isSubmitting && (
						<div className='center' style={{ marginTop: '1rem' }}>
							<LoadingSpinner />
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default PlayerMerge
